import React, { useState, useEffect } from 'react';

import { Outlet, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, Dispatch } from '../../store';

import Typography from '../../components/Typography/Typography';
import ASSETS from '../../assets';
import Button from '../../components/Button/Button';
import Profile from '../../dialogs/Profile/Profile';
import { getCurrentUser } from '../../http';

const DashboardLayout = () => {
  const [openProfile, setOpenProfile] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const dispatch = useDispatch<Dispatch>();

  const { user } = useSelector((state: RootState) => state.auth);

  const handleProfile = () => {
    setOpenProfile(true);
  };

  

  // Fetch user data on component mount if user data is incomplete
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoadingUser(true);
        const { data } = await getCurrentUser();
        dispatch.auth.setUser(data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    // Only fetch if user data is missing or incomplete (no firstName/lastName)
    if (!user || (!user.firstName && !user.lastName)) {
      fetchUserData();
    }
  }, [dispatch.auth]); // Remove 'user' from dependencies to prevent infinite loops

  // Fetch user data whenever profile modal opens
  useEffect(() => {
    if (openProfile) {
      const fetchUserData = async () => {
        try {
          setIsLoadingUser(true);
          const { data } = await getCurrentUser();
          dispatch.auth.setUser(data.user);
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setIsLoadingUser(false);
        }
      };
      
      fetchUserData();
    }
  }, [openProfile, dispatch.auth]);

  const navigate = useNavigate();
  
  const onClickHandler = async () => {
    try {
      // Check if user is coming from root path
      const currentPath = window.location.pathname;
      if (currentPath === '/') {
        // Fetch user data when redirecting from root to documents
        setIsLoadingUser(true);
        const { data } = await getCurrentUser();
        dispatch.auth.setUser(data.user);
        setIsLoadingUser(false);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setIsLoadingUser(false);
    }
    navigate(`/documents`);
  }

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      <div className='h-[168px]  bg-g_gradient'>
        <div className='h-full  pb-[51px] flex items-center justify-center gap-5 bg-f_gradient'>
          <div className='w-full max-w-[980px] px-5 flex items-center justify-between'>
            <div className='flex items-center gap-5 cursor-pointer' onClick={onClickHandler}>
              <img src={ASSETS.kpmg_logo} alt='' />
              <Typography variant='h3' color='white'>
                +
              </Typography>
              <img src={ASSETS.kresus_logo} alt='' />
            </div>
            <div className='flex items-center gap-[25px]'>
              <div className='flex flex-col items-end'>
                  <Typography variant='h6' color='white' className='flex items-center gap-1'>
                    {user?.verificationStatus && (
                      <img src={ASSETS.onchain_verified} alt='verified' className='h-4 w-4' />
                    )}
                    {isLoadingUser ? (
                      "Loading..."
                    ) : (
                      <>
                        {user?.firstName ?? "User"} {user?.lastName ?? "name"}
                      </>
                    )}
                  </Typography>

                <Typography 
                  variant='body1' 
                  color='lightBlue'
                  className='cursor-pointer hover:underline'
                >
                  KPMG
                </Typography>
              </div>
              <Button
                label='Profile'
                textColor='white'
                leftIcon={ASSETS.settings}
                onClick={handleProfile}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex-grow flex flex-col w-full bg-white '>
        <div className='flex-grow w-full max-w-[980px] mx-auto mt-[-51px] flex flex-col'>
          <Outlet />
        </div>
      </div>
      {openProfile && <Profile open={openProfile} setOpen={setOpenProfile} />}
    </div>
  );
};

export default DashboardLayout;
